import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Eye, Settings, TrendingUp, Target, BookOpen, LogOut, X, Loader2, FastForward, RotateCcw } from "lucide-react";
import {
  WelcomeScreen,
  VendorIntro,
  InteractiveCriterion,
  FinalSummary,
  JourneyProgress,
  ClickWaveProvider,
} from "@/components/experience";
import { api, type VendorEvaluation, type EvaluationScore, type LLMModelId } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import LLMSelectionModal from "@/components/modals/LLMSelectionModal";
import { GLEAN_DEMO_EVALUATION_ID, GLEAN_MOCK_EVALUATION } from "@/data/glean-mock";

type Stage = "loading" | "error" | "welcome" | "vendor-intro" | "criteria" | "summary";

// Per-criterion brand colors (main + darker shade for gradients)
const CRITERION_COLORS: Record<string, { main: string; dark: string }> = {
  SEE: { main: "#4DA6D4", dark: "#3B8DB8" },
  CHANGE: { main: "#9B4FD4", dark: "#7E3AB5" },
  ADAPT: { main: "#5BBFB5", dark: "#45A199" },
  USE: { main: "#C47B9F", dark: "#A86485" },
  LEARN: { main: "#5C6BC0", dark: "#4A57A3" },
  EXIT: { main: "#C4785A", dark: "#A86245" },
};

const CRITERION_CONFIG: Record<string, { icon: React.ReactNode; color: string; accentColor: string; description: string; subdescription: string }> = {
  SEE: {
    icon: <Eye className="w-12 h-12 text-white" />,
    color: "from-[#4DA6D4] to-[#3B8DB8]",
    accentColor: "#4DA6D4",
    description: "Can you see how the AI works?",
    subdescription: "Evaluates whether you can see the prompts, models, and architecture being used.",
  },
  CHANGE: {
    icon: <Settings className="w-12 h-12 text-white" />,
    color: "from-[#9B4FD4] to-[#7E3AB5]",
    accentColor: "#9B4FD4",
    description: "Can you modify AI behavior?",
    subdescription: "Assesses your ability to adjust prompts, models, and configurations.",
  },
  ADAPT: {
    icon: <TrendingUp className="w-12 h-12 text-white" />,
    color: "from-[#5BBFB5] to-[#45A199]",
    accentColor: "#5BBFB5",
    description: "Does the vendor keep pace with AI advancement?",
    subdescription: "Measures how quickly the vendor adopts new AI capabilities.",
  },
  USE: {
    icon: <Target className="w-12 h-12 text-white" />,
    color: "from-[#C47B9F] to-[#A86485]",
    accentColor: "#C47B9F",
    description: "Is the tool actually useful?",
    subdescription: "Determines if the tool provides real value versus the appearance of value.",
  },
  LEARN: {
    icon: <BookOpen className="w-12 h-12 text-white" />,
    color: "from-[#5C6BC0] to-[#4A57A3]",
    accentColor: "#5C6BC0",
    description: "Do you build portable AI skills?",
    subdescription: "Evaluates whether using the tool builds generalizable AI capabilities.",
  },
  EXIT: {
    icon: <LogOut className="w-12 h-12 text-white" />,
    color: "from-[#C4785A] to-[#A86245]",
    accentColor: "#C4785A",
    description: "Can you leave cleanly?",
    subdescription: "Assesses the difficulty of transitioning away from the vendor.",
  },
};

// Mock data for demo mode (when no evaluationId is provided)
const MOCK_EVALUATION: VendorEvaluation = {
  id: "demo",
  vendor_id: "demo-vendor",
  vendor_name: "Nexus AI",
  vendor_category: "Enterprise Knowledge Management & AI Search",
  evaluation_date: new Date().toISOString(),
  executive_summary: {
    key_takeaway: "Nexus AI delivers strong enterprise search capabilities with genuine utility, but presents meaningful trade-offs in transparency, customizability, and vendor lock-in that warrant careful consideration.",
    sentiment: "neutral",
    paragraphs: [
      "Nexus AI solves a real problem effectively. Organizations struggling with fragmented knowledge systems and information silos will find immediate value in its cross-platform search and synthesis capabilities.",
      "The principal concerns center on organizational learning and long-term flexibility. Nexus operates as a black box—users cannot modify core AI behaviors or select alternative models.",
      "Additionally, proprietary data formats create significant switching costs. Organizations should enter this relationship with clear awareness of these dependencies.",
    ],
  },
  criteria: [
    {
      criterion_id: "SEE",
      criterion_name: "SEE",
      subtitle: "Transparency & Visibility",
      score: "no",
      assessment: "Nexus provides no visibility into its AI operations. System prompts, model selection logic, and context handling approaches are not exposed to users. Documentation describes features but not mechanics.",
      tradeoff: { gain: "Simplified interface without technical complexity", giveUp: "Ability to verify vendor optimization choices align with organizational interests; Zero organizational learning about AI mechanics" },
      questions: ["What visibility can enterprise customers obtain into system prompts and model selection?", "Is transparency on the product roadmap?"],
      why_it_matters: ["Vendors control many variables that affect output quality.", "Understanding how AI tools work is essential organizational knowledge.", "Configuration decisions will be consequential."],
      sources: [
        { uri: "https://nexusai.example.com/docs/architecture", title: "Architecture Overview", snippet: "Our AI pipeline uses proprietary optimization techniques to deliver the best results." },
        { uri: "https://nexusai.example.com/security", title: "Security & Privacy", snippet: "System prompts and model configurations are protected as trade secrets." },
      ],
    },
    {
      criterion_id: "CHANGE",
      criterion_name: "CHANGE",
      subtitle: "Customizability & Control",
      score: "partially",
      assessment: "Users can adjust certain retrieval parameters and create custom knowledge domains. However, the core AI layer cannot be modified.",
      tradeoff: { gain: "Meaningful search behavior customization without technical overhead", giveUp: "Ability to optimize core AI layer for specific organizational needs" },
      questions: ["What additional customization capabilities are planned?", "Can enterprise customers access prompt modification?"],
      why_it_matters: ["Every organization has different needs.", "Customizability transforms a tool into something you learn from."],
      sources: [
        { uri: "https://nexusai.example.com/docs/customization", title: "Customization Guide", snippet: "Create knowledge domains to scope searches to specific document collections." },
      ],
    },
    {
      criterion_id: "ADAPT",
      criterion_name: "ADAPT",
      subtitle: "Vendor Agility & Innovation",
      score: "partially",
      assessment: "Review of Nexus's release history shows two underlying model updates within the past 18 months. However, the update cycle lags frontier model releases by 3-6 months.",
      tradeoff: { gain: "Vendor-managed updates without migration burden", giveUp: "Ability to move at the pace of AI advancement" },
      questions: [],
      why_it_matters: ["AI is advancing at an extraordinary pace.", "The gap between frontier capabilities and vendor implementation impacts competitive advantage."],
      sources: [
        { uri: "https://nexusai.example.com/changelog", title: "Release Notes", snippet: "Version 3.2 (March 2024): Upgraded synthesis engine to GPT-4 class models." },
      ],
    },
    {
      criterion_id: "USE",
      criterion_name: "USE",
      subtitle: "Genuine Utility & Value",
      score: "yes",
      assessment: "Nexus AI demonstrates clear utility. Customer case studies confirm the tool meaningfully improves information retrieval. This is the vendor's core strength.",
      tradeoff: { gain: "Effective solution to a common, costly enterprise problem", giveUp: "" },
      questions: [],
      why_it_matters: ["AI systems are good at creating the impression of usefulness.", "The fundamental question: Does this tool provide more value than it costs?"],
      sources: [
        { uri: "https://nexusai.example.com/case-studies", title: "Case Studies", snippet: "ACME Corp reported 40% reduction in time spent searching for information." },
      ],
    },
    {
      criterion_id: "LEARN",
      criterion_name: "LEARN",
      subtitle: "Transferable Knowledge",
      score: "no",
      assessment: "Using Nexus AI teaches users how to use Nexus AI—and nothing more. The interface completely abstracts away AI concepts.",
      tradeoff: { gain: "Easy onboarding with minimal training requirements", giveUp: "Opportunity to build portable AI skills; Workforce remains dependent on this vendor" },
      questions: [],
      why_it_matters: ["A tool that teaches only itself creates deep lock-in.", "Companies that thrive will develop deep organizational fluency in AI."],
      sources: [
        { uri: "https://nexusai.example.com/getting-started", title: "Getting Started", snippet: "Simply type your question in natural language. Our AI handles everything else." },
      ],
    },
    {
      criterion_id: "EXIT",
      criterion_name: "EXIT",
      subtitle: "Portability & Migration",
      score: "no",
      assessment: "Organizational knowledge can only be exported in Nexus's proprietary format. Migration would require rebuilding knowledge structures from scratch.",
      tradeoff: { gain: "", giveUp: "Clean migration path; All organizational investment trapped with vendor" },
      questions: ["What is your data portability policy?", "Can enrichments be exported in standard formats?"],
      why_it_matters: ["Lock-in is never in the customer's interest.", "A vendor that traps value forecloses modular AI capabilities."],
      sources: [
        { uri: "https://nexusai.example.com/docs/export", title: "Data Export", snippet: "Export your knowledge base in Nexus Archive Format (.naf) - proprietary format." },
      ],
    },
  ],
  key_questions: [
    "How critical is enterprise search as an immediate pain point?",
    "Is your organization actively building AI capacity now?",
    "What is your tolerance for vendor lock-in?",
    "Would this tool be a primary AI touchpoint for employees?",
    "How do you evaluate the trade-off between immediate productivity and long-term capability?",
  ],
  status: "completed",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

/**
 * Interactive Experience Page - Fullscreen immersive vendor evaluation journey.
 * This page breaks out of the dashboard layout for a true fullscreen experience.
 */
export default function InteractiveExperiencePage() {
  const navigate = useNavigate();
  const { evaluationId } = useParams<{ evaluationId?: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [evaluation, setEvaluation] = useState<VendorEvaluation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>(evaluationId ? "loading" : "welcome");
  const [currentCriterionIndex, setCurrentCriterionIndex] = useState(0);
  const [isEntering, setIsEntering] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [skipExplanations, setSkipExplanations] = useState(false);
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);

  // Fetch evaluation data if evaluationId is provided
  useEffect(() => {
    if (!evaluationId) {
      setEvaluation(MOCK_EVALUATION);
      return;
    }

    // Use hardcoded Glean mock data for the demo
    if (evaluationId === GLEAN_DEMO_EVALUATION_ID) {
      setEvaluation(GLEAN_MOCK_EVALUATION);
      setStage("welcome");
      return;
    }

    async function fetchEvaluation() {
      if (!evaluationId) return;
      
      const id = evaluationId; // TypeScript type narrowing
      try {
        setStage("loading");
        const { evaluation } = await api.getEvaluation(id);
        setEvaluation(evaluation);
        setStage("welcome");
      } catch (err) {
        console.error("Failed to fetch evaluation:", err);
        setError(err instanceof Error ? err.message : "Failed to load evaluation");
        setStage("error");
      }
    }

    fetchEvaluation();
  }, [evaluationId]);

  // Transform evaluation criteria to component format
  const criteriaData = useMemo(() => {
    if (!evaluation) return [];

    return evaluation.criteria.map((c) => {
      const config = CRITERION_CONFIG[c.criterion_id] || {
        icon: <Eye className="w-12 h-12 text-white" />,
        color: "from-slate-500 to-slate-600",
        accentColor: "#94a3b8",
        description: c.subtitle,
        subdescription: "",
      };

      return {
        title: c.criterion_name,
        subtitle: c.subtitle,
        icon: config.icon,
        color: config.color,
        accentColor: config.accentColor,
        description: config.description,
        subdescription: config.subdescription,
        whyItMatters: c.why_it_matters,
        score: c.score as "yes" | "partially" | "no",
        assessment: c.assessment,
        tradeoffGain: c.tradeoff.gain,
        tradeoffLose: c.tradeoff.giveUp,
        questions: c.questions,
        sources: c.sources,
      };
    });
  }, [evaluation]);

  const handleStartJourney = () => {
    setStage("vendor-intro");
  };

  const handleBeginDeepDive = () => {
    setStage("criteria");
    setCurrentCriterionIndex(0);
  };

  const handleNextCriterion = () => {
    if (currentCriterionIndex < criteriaData.length - 1) {
      setCurrentCriterionIndex(currentCriterionIndex + 1);
    } else {
      setStage("summary");
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Step 0 is Introduction (welcome/framework/vendor-intro)
    if (stepIndex === 0) {
      setStage("vendor-intro");
      setCurrentCriterionIndex(0);
      return;
    }

    // Last step is Summary
    if (stepIndex === journeySteps.length - 1) {
      setStage("summary");
      return;
    }

    // Steps 1-6 are criteria (stepIndex - 1 because step 0 is intro)
    const criterionIndex = stepIndex - 1;
    if (criterionIndex >= 0 && criterionIndex < criteriaData.length) {
      setStage("criteria");
      setCurrentCriterionIndex(criterionIndex);
    }
  };

  const handleRestart = () => {
    setStage("welcome");
    setCurrentCriterionIndex(0);
  };

  const handleExit = () => {
    setIsExiting(true);
    setTimeout(() => {
      navigate('/analysis');
    }, 800);
  };

  const handlePrepareCall = () => {
    if (!evaluation) return;
    const params = new URLSearchParams({ vendorId: evaluation.vendor_id });
    setIsExiting(true);
    setTimeout(() => {
      navigate(`/vendor-call?${params.toString()}`);
    }, 800);
  };

  const handleRerunEvaluation = async (model: LLMModelId) => {
    if (!evaluation) return;
    setIsRerunning(true);
    setShowRerunModal(false);
    try {
      await api.startEvaluation(evaluation.vendor_id, { model });
      navigate("/analysis");
    } catch (err) {
      console.error("Failed to rerun evaluation:", err);
      setIsRerunning(false);
    }
  };

  // Handle enter transition
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsEntering(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === "Space" && stage === "vendor-intro") {
        e.preventDefault();
        handleBeginDeepDive();
      }
      if (e.code === "Escape") {
        handleExit();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [stage]);

  // Journey steps for progress indicator
  const journeySteps = [
    { title: "Introduction", completed: stage !== "welcome" && stage !== "vendor-intro" && stage !== "loading" && stage !== "error" },
    ...criteriaData.map((c, i) => ({
      title: c.title,
      completed: i < currentCriterionIndex || stage === "summary"
    })),
    { title: "Summary", completed: stage === "summary" }
  ];

  const getCurrentStepIndex = () => {
    if (stage === "loading" || stage === "error" || stage === "welcome" || stage === "vendor-intro") return 0;
    if (stage === "criteria") return currentCriterionIndex + 1;
    if (stage === "summary") return journeySteps.length - 1;
    return 0;
  };

  // Format evaluation date
  const evaluationDate = evaluation
    ? new Date(evaluation.evaluation_date).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "";

  return (
    <ClickWaveProvider>
      <div className="min-h-screen">

        {/* Enter transition */}
        <AnimatePresence>
          {isEntering && (
            <motion.div
              className="fixed inset-0 z-[100] pointer-events-none bg-[#063F6B]"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            />
          )}
        </AnimatePresence>

        {/* Exit transition */}
        <AnimatePresence>
          {isExiting && (
            <motion.div
              className="fixed inset-0 z-[100] pointer-events-none bg-[#063F6B]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            />
          )}
        </AnimatePresence>
        
        {/* Exit button */}
        <button
          onClick={handleExit}
          disabled={isExiting}
          className="fixed top-[calc(1rem+var(--env-banner-height,0px))] left-4 sm:top-[calc(1.5rem+var(--env-banner-height,0px))] sm:left-6 z-50 p-3 bg-white/[0.08] backdrop-blur-sm rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-[#EAA059]/30 transition-colors duration-200 disabled:opacity-50"
          title="Exit to Reports (Esc)"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Rerun Evaluation button (admin only, hidden for Glean demo) */}
        {isAdmin && evaluation && evaluationId !== GLEAN_DEMO_EVALUATION_ID && (
          <button
            onClick={() => setShowRerunModal(true)}
            disabled={isRerunning || isExiting}
            className="fixed top-[calc(1rem+var(--env-banner-height,0px))] right-4 sm:top-[calc(1.5rem+var(--env-banner-height,0px))] sm:right-6 z-50 flex items-center gap-2 px-4 py-2.5 sm:py-2 bg-white/[0.08] backdrop-blur-sm rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-[#EAA059]/30 transition-colors duration-200 disabled:opacity-50 text-sm font-medium"
            title="Rerun Evaluation"
          >
            {isRerunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            <span className="hidden sm:inline">{isRerunning ? "Starting..." : "Rerun"}</span>
          </button>
        )}

        {/* Skip explanations toggle */}
        {(stage === "criteria" || stage === "summary") && (
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed top-[calc(4.5rem+var(--env-banner-height,0px))] left-4 sm:top-[calc(1.5rem+var(--env-banner-height,0px))] sm:left-20 z-50"
          >
            <label className="flex items-center gap-2 sm:gap-3 px-3 py-2 sm:px-4 bg-white/[0.08] backdrop-blur-sm rounded-full border border-white/10 cursor-pointer hover:bg-white/[0.12] hover:border-[#EAA059]/30 transition-colors duration-200">
              <FastForward className={`w-4 h-4 transition-colors ${skipExplanations ? 'text-[#EAA059]' : 'text-slate-400'}`} />
              <span className="text-xs sm:text-sm text-slate-300">Skip Explanations</span>
              <input
                type="checkbox"
                checked={skipExplanations}
                onChange={(e) => setSkipExplanations(e.target.checked)}
                className="sr-only"
              />
              <div className={`relative w-10 h-5 rounded-full transition-colors ${skipExplanations ? 'bg-[#EAA059]' : 'bg-white/10'}`}>
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${skipExplanations ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
            </label>
          </motion.div>
        )}
        
        {/* Show progress indicator during criteria and summary */}
        {(stage === "criteria" || stage === "summary") && (
          <JourneyProgress
            currentStep={getCurrentStepIndex()}
            totalSteps={journeySteps.length}
            steps={journeySteps}
            onStepClick={handleStepClick}
          />
        )}

        {/* Loading state */}
        {stage === "loading" && (
          <div className="min-h-screen bg-[#063F6B] flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-[#EAA059] animate-spin mx-auto mb-4" />
              <p className="text-slate-300">Loading evaluation...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {stage === "error" && (
          <div className="min-h-screen bg-[#063F6B] flex items-center justify-center">
            <div className="text-center max-w-md px-6">
              <div className="w-14 h-14 bg-rose-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-7 h-7 text-rose-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Failed to Load Evaluation</h2>
              <p className="text-slate-400 mb-6">{error}</p>
              <button
                onClick={handleExit}
                className="px-6 py-3 bg-[#EAA059] hover:bg-[#d4914d] text-white rounded-full font-medium transition-colors"
              >
                Return to Reports
              </button>
            </div>
          </div>
        )}

        {stage === "welcome" && <WelcomeScreen onStart={handleStartJourney} />}
        
        {stage === "vendor-intro" && evaluation && (
          <VendorIntro
            vendorName={evaluation.vendor_name}
            category={evaluation.vendor_category}
            evaluationDate={evaluationDate}
            onBegin={handleBeginDeepDive}
          />
        )}
        
        {stage === "criteria" && criteriaData.length > 0 && (
          <InteractiveCriterion
            key={currentCriterionIndex}
            {...criteriaData[currentCriterionIndex]}
            onNext={handleNextCriterion}
            currentIndex={currentCriterionIndex}
            totalCount={criteriaData.length}
            skipExplanations={skipExplanations}
          />
        )}
        
        {stage === "summary" && evaluation && (
          <FinalSummary
            vendorName={evaluation.vendor_name}
            criteria={criteriaData.map(c => ({
              title: c.title,
              subtitle: c.subtitle,
              description: c.description,
              subdescription: c.subdescription,
              score: c.score,
              icon: c.icon,
              accentColor: c.accentColor,
            }))}
            recommendation={evaluation.executive_summary.key_takeaway}
            onRestart={handleRestart}
            onExit={handleExit}
            onPrepareCall={evaluationId && evaluationId !== GLEAN_DEMO_EVALUATION_ID ? handlePrepareCall : undefined}
          />
        )}
        {/* LLM Selection Modal for Rerun (admin only) */}
        <LLMSelectionModal
          isOpen={showRerunModal}
          onClose={() => setShowRerunModal(false)}
          onConfirm={handleRerunEvaluation}
          title="Rerun Evaluation"
        />
      </div>
    </ClickWaveProvider>
  );
}
